import { useEffect, useState } from "react";

import { addYears } from "date-fns";
import { CalendarDays } from "lucide-react";

import { DateTimeInputV2 } from "@/components/pieces/DateTimeInputV2";
import { CompletionRequirements } from "@/components/pieces/roleplays/CompletionRequirements";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogInner,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { SUBTEXT_COLOR } from "@/css/constants";
import {
  ScenarioAssignmentType,
  useUpdateScenarioAssignmentMutation,
} from "@/graphql";
import { getBrowserTimezone } from "@/utils/datetime";

interface EditModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  assignment: ScenarioAssignmentType;
  onSuccess: () => void;
}

export function EditModal({
  open,
  onOpenChange,
  assignment,
  onSuccess,
}: EditModalProps) {
  const [dueDate, setDueDate] = useState<Date | undefined>(
    assignment.dueDate ? new Date(assignment.dueDate) : undefined,
  );
  const [scenarioRankMin, setScenarioRankMin] = useState<string | undefined>(
    assignment.scenarioRankMin || undefined,
  );
  const [scenarioAttemptMin, setScenarioAttemptMin] = useState<
    number | undefined
  >(assignment.scenarioAttemptMin || undefined);
  const [scenarioAttemptMax, setScenarioAttemptMax] = useState<
    number | undefined
  >(assignment.scenarioAttemptMax || undefined);
  const [timezone, setTimezone] = useState(getBrowserTimezone());
  const maxDate = addYears(new Date(), 1);
  const minDate = new Date();

  useEffect(() => {
    setDueDate(assignment.dueDate ? new Date(assignment.dueDate) : undefined);
    setScenarioRankMin(assignment.scenarioRankMin || undefined);
    setScenarioAttemptMin(assignment.scenarioAttemptMin || undefined);
    setScenarioAttemptMax(assignment.scenarioAttemptMax || undefined);
  }, [assignment]);

  const [updateAssignment, { loading }] = useUpdateScenarioAssignmentMutation();

  const handleSave = async () => {
    try {
      await updateAssignment({
        variables: {
          assignmentId: assignment.id,
          dueDate: dueDate?.toISOString() || null,
          scenarioAttemptMin: scenarioAttemptMin || null,
          scenarioAttemptMax: scenarioAttemptMax || null,
          scenarioRankMin: scenarioRankMin || null,
        },
      });
      onSuccess();
      onOpenChange(false);
    } catch (error) {
      console.error("Failed to update assignment:", error);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="w-[550px] p-0 border border-gray-200 rounded-lg"
        onOpenAutoFocus={(e) => e.preventDefault()}
      >
        {/* Header */}
        <DialogHeader>
          <DialogTitle>Edit Assignment</DialogTitle>
        </DialogHeader>
        <DialogInner>
          {/* Content */}
          <div className="space-y-4">
            {/* Due Date Section */}
            <div className="space-y-4">
              <Label className="text-base font-semibold mb-1">Due Date</Label>
              <div className="flex gap-3 w-full">
                <div style={{ position: "relative", width: "100%" }}>
                  <CalendarDays
                    style={{
                      position: "absolute",
                      top: 12,
                      left: 10,
                      zIndex: 99,
                      color: SUBTEXT_COLOR,
                    }}
                    strokeWidth={1.5}
                  />
                  <DateTimeInputV2
                    onChange={(date) => {
                      setDueDate(date ? new Date(date) : undefined);
                    }}
                    value={dueDate?.toISOString() ?? null}
                    timezone={timezone}
                    onChangeTimezone={setTimezone}
                    maxDateTime={maxDate}
                    minDateTime={minDate}
                    containerStyles={{
                      height: "50px",
                      width: "100%",
                    }}
                  />
                </div>
              </div>
            </div>

            {/* Completion Requirements Section */}
            <CompletionRequirements
              className="pt-4"
              scenarioRankMin={scenarioRankMin}
              scenarioAttemptMin={scenarioAttemptMin}
              scenarioAttemptMax={scenarioAttemptMax}
              onRankMinChange={setScenarioRankMin}
              onAttemptMinChange={setScenarioAttemptMin}
              onAttemptMaxChange={setScenarioAttemptMax}
              showTitle={true}
            />
          </div>

          {/* Footer */}
          <div className="flex justify-end pt-4">
            <Button
              intent="primary"
              size="sm"
              onClick={handleSave}
              disabled={loading}
              className="w-[100px] h-10"
            >
              {loading ? "Saving..." : "Save"}
            </Button>
          </div>
        </DialogInner>
      </DialogContent>
    </Dialog>
  );
}
